/**
 * @file Interrupteurs AutoMove / AutoFire (reglages joueur), grises et inactifs si le mode courant les force (hub : tout force a off).
 */
import type { SettingsData } from '@core/index';
import { Component } from '../core/Component';
import { h, toggleClass } from '../core/dom';
import { uiIcon } from '../core/uiIcons';
import type { UiIconId } from '../core/uiIcons';
import { send } from '../core/feedback';
import type { UiContext } from '../UiContext';

type ToggleKey = Extract<keyof SettingsData, 'autoMove' | 'autoFire'>;

interface ToggleView {
  root: HTMLElement;
  state: HTMLElement;
}

export class ControlToggles extends Component {
  private readonly views = new Map<ToggleKey, ToggleView>();

  constructor(ctx: UiContext) {
    super(ctx, 'div', 'controls');
  }

  protected build(): void {
    const { i18n } = this.ctx;
    const defs: Array<{ key: ToggleKey; label: string; icon: UiIconId }> = [
      { key: 'autoMove', label: i18n.t('hud.autoMove'), icon: 'compass' },
      { key: 'autoFire', label: i18n.t('hud.autoFire'), icon: 'gun' },
    ];
    for (const d of defs) {
      const state = h('span', { class: 'toggle-state' });
      const root = h(
        'button',
        {
          class: `toggle toggle-${d.key}`,
          type: 'button',
          role: 'switch',
          'aria-label': d.label,
          'data-ui-block': true,
          'data-fast': true,
          onclick: () => {
            if (root.classList.contains('forced')) return;
            const current = this.ctx.sim.data.settings[d.key];
            void send(this.ctx, { type: 'settings.set', patch: d.key === 'autoMove' ? { autoMove: !current } : { autoFire: !current } });
            this.update(0);
          },
        },
        h('span', { class: 'toggle-icon' }, uiIcon(d.icon)),
        h('span', { class: 'toggle-label' }, d.label),
        state,
      );
      this.views.set(d.key, { root, state });
      this.el.append(root);
    }
    this.update(0);
  }

  override update(_dt: number): void {
    const view = this.ctx.sim.getView();
    for (const [key, v] of this.views) {
      const forced = view.rules.control[key] !== 'player';
      const on = view.control[key];
      toggleClass(v.root, 'forced', forced);
      toggleClass(v.root, 'on', on);
      v.root.setAttribute('aria-checked', String(on));
      v.root.setAttribute('aria-disabled', String(forced));
    }
  }
}
